import {
    type FieldValue,
    type ProviderFieldSchema,
    type ProviderGroup,
    type ProviderRow,
    type ProviderSchema,
    SECRET_NAMES,
    type SecretName,
    secretNamesOf,
} from '@/domain/providerConfig';
import { sameValue } from '@/routes/shared/ProviderConfig/form/formLeaves';

import type { FormDiff, PenDiff } from './formDiff';

/** A pen as the save would leave it, which is what the client rules judge, as the server's do. */
export interface EffectivePen {
    cageId: number;
    isActive: boolean;
    /** The stored row, or null for a pen the save creates. */
    row: ProviderRow | null;
    diff: PenDiff | null;
    values: Record<string, FieldValue>;
    secretsSet: Record<SecretName, boolean>;
}

export interface EffectiveSave {
    pens: EffectivePen[];
    /** Pens the save writes; a site change writes every row. */
    touched: Set<number>;
}

/** A row's stored value; the read shows site values once for the site, or per pen if they differ. */
export function storedValue(
    group: ProviderGroup,
    row: ProviderRow,
    field: ProviderFieldSchema
): FieldValue {
    if (field.scope === 'pen') return row.fields[field.key] ?? null;
    const connection = group.connection[field.key];
    if (connection === undefined) return null;
    return connection.varies ? (connection.perPen[row.cageId] ?? null) : connection.value;
}

function isDestination(field: ProviderFieldSchema): boolean {
    return field.kind === 'url' || field.kind === 'host';
}

export function destinationFields(schema: ProviderSchema): ProviderFieldSchema[] {
    return schema.fields.filter(isDestination);
}

/** R13 narrowed by R20: a new pen reuses the site credential only where every row is sent. */
function matchesSiteDestination(
    schema: ProviderSchema,
    group: ProviderGroup | null,
    values: Record<string, FieldValue>
): boolean {
    const rows = group?.rows ?? [];
    if (group === null || rows.length === 0) return false;
    return destinationFields(schema).every((field) => {
        const first = storedValue(group, rows[0], field);
        return (
            rows.every((row) => sameValue(storedValue(group, row, field), first)) &&
            sameValue(values[field.key] ?? null, first)
        );
    });
}

/** Secrets the save types or clears on an existing pen are no longer one site value. */
function changingOnExistingPens(diff: FormDiff): Set<SecretName> {
    const names = new Set<SecretName>();
    for (const pen of Object.values(diff.pens))
        if (!pen.create)
            for (const name of SECRET_NAMES) if (pen.secrets[name] !== undefined) names.add(name);
    return names;
}

function namedSecret(
    diff: FormDiff,
    pen: PenDiff | null,
    name: SecretName
): string | null | undefined {
    const own = pen?.secrets[name];
    return own !== undefined ? own : diff.secrets[name];
}

function storedPen(
    group: ProviderGroup,
    row: ProviderRow,
    diff: FormDiff,
    fansOut: boolean
): EffectivePen {
    const pen = diff.pens[row.cageId] ?? null;
    const values: Record<string, FieldValue> = { ...row.fields };
    for (const [key, connection] of Object.entries(group.connection))
        values[key] = connection.varies
            ? (connection.perPen[row.cageId] ?? null)
            : connection.value;
    if (pen !== null || fansOut) Object.assign(values, diff.connection);
    if (pen !== null) Object.assign(values, pen.fields);
    const secretsSet = { apiKey: false, clientId: false, clientSecret: false };
    for (const name of SECRET_NAMES) {
        const named = namedSecret(diff, pen, name);
        secretsSet[name] = named === undefined ? (row.credentials[name] ?? false) : named !== null;
    }
    return {
        cageId: row.cageId,
        isActive: pen?.isActive ?? row.isActive,
        row,
        diff: pen,
        values,
        secretsSet,
    };
}

function createdPen(
    schema: ProviderSchema,
    group: ProviderGroup | null,
    diff: FormDiff,
    cageId: number,
    pen: PenDiff
): EffectivePen {
    const values: Record<string, FieldValue> = { ...diff.connection, ...pen.fields };
    const inherits = matchesSiteDestination(schema, group, values);
    const changing = changingOnExistingPens(diff);
    const secretsSet = { apiKey: false, clientId: false, clientSecret: false };
    for (const name of secretNamesOf(schema)) {
        const named = namedSecret(diff, pen, name);
        const shared = group?.credentials[name]?.state === 'set' && !changing.has(name);
        secretsSet[name] = named === undefined ? inherits && shared : named !== null;
    }
    return { cageId, isActive: pen.isActive ?? true, row: null, diff: pen, values, secretsSet };
}

/** Every pen row the save leaves, removed ones out, created ones in. */
export function effectiveSave(
    schema: ProviderSchema,
    group: ProviderGroup | null,
    diff: FormDiff
): EffectiveSave {
    const fansOut = Object.keys(diff.connection).length > 0 || Object.keys(diff.secrets).length > 0;
    const pens: EffectivePen[] = [];
    const touched = new Set<number>();
    const stored = new Set<number>();
    if (group !== null)
        for (const row of group.rows) {
            stored.add(row.cageId);
            const pen = diff.pens[row.cageId];
            if (pen !== undefined || fansOut) touched.add(row.cageId);
            if (!pen?.remove) pens.push(storedPen(group, row, diff, fansOut));
        }
    for (const [key, pen] of Object.entries(diff.pens)) {
        const cageId = Number(key);
        if (!pen.create || stored.has(cageId)) continue;
        touched.add(cageId);
        pens.push(createdPen(schema, group, diff, cageId, pen));
    }
    return { pens, touched };
}

/**
 * What a pen's own secret box should say while it is left as it is, from the same prediction
 * the save rules use: `stored` keeps the pen's stored secret, `inherited` gets the site's (a new
 * pen, or a typed site secret), `clearedWithSite` loses it to a site Clear, `none` holds nothing.
 */
export type SecretOutlook = 'stored' | 'inherited' | 'clearedWithSite' | 'none';

export function penSecretOutlook(
    schema: ProviderSchema,
    group: ProviderGroup | null,
    diff: FormDiff
): ReadonlyMap<number, Readonly<Record<SecretName, SecretOutlook>>> {
    const outlook = new Map<number, Record<SecretName, SecretOutlook>>();
    for (const pen of effectiveSave(schema, group, diff).pens) {
        const own = pen.diff?.secrets ?? {};
        const entry: Record<SecretName, SecretOutlook> = {
            apiKey: 'none',
            clientId: 'none',
            clientSecret: 'none',
        };
        for (const name of SECRET_NAMES) {
            const stored = pen.row?.credentials[name] === true;
            if (own[name] !== undefined) entry[name] = stored ? 'stored' : 'none';
            // A site Clear takes only what a pen holds; one that never had it stays without.
            else if (pen.row !== null && diff.secrets[name] === null)
                entry[name] = stored ? 'clearedWithSite' : 'none';
            else if (stored) entry[name] = 'stored';
            else entry[name] = pen.secretsSet[name] ? 'inherited' : 'none';
        }
        outlook.set(pen.cageId, entry);
    }
    return outlook;
}
